import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, Container } from '@mui/material';
import ParentForm from '../components/forms/ParentForm';
import ChildrenForm, { ChildrenFormHandle } from '../components/forms/ChildrenForm';
import { useUser } from '../contexts/UserContext';
import { Link, useNavigate } from 'react-router-dom';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import { GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { ActionConfirmationVariant } from '../types';
import createUser from '../hooks/user/createUser';
import { isValidPassword } from '../utils/index';
import { useScrollToConfirmation } from '../hooks/useScrollToConfirmation';


const SignUpPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({ visible: false, variant: 'info', message: '' });
  const [parentFormData, setParentFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [childrenData, setChildrenData] = useState([]);
  const [loading, setLoading] = useState(false);
  const childFormRef = useRef<ChildrenFormHandle | null>(null);

  const handleParentDataChange = (data: React.SetStateAction<{ fname: string; lname: string; email: string; phone: string; password: string; confirmPassword: string; }>) => {
    setParentFormData(data);
  };

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();
  useScrollToConfirmation(confirmation.visible, confirmation.message, 'signup-confirmation');

  const handleRecaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      setConfirmation({ visible: true, variant: 'error', message: 'Please complete the reCAPTCHA verification.' });
      return;
    }
    const recTokenResult = await executeRecaptcha('signUpForm');
    setRecaptchaToken(recTokenResult);
  }, [executeRecaptcha]);

  const handleSave = async () => {
    setLoading(true);

    await handleRecaptchaVerify();
    if (!recaptchaToken) {
      setConfirmation({ visible: true, variant: 'error', message: 'Please complete the reCAPTCHA verification.' });
      setLoading(false);
      return;
    }

    if (!parentFormData.fname || !parentFormData.lname || !parentFormData.email || !parentFormData.phone) {
      setConfirmation({ visible: true, variant: 'error', message: 'All parent fields are required.' });
      setLoading(false);
      return;
    }

    if (parentFormData.password !== parentFormData.confirmPassword) {
      setConfirmation({ visible: true, variant: 'error', message: 'Password and Confirm Password do not match.' });
      setLoading(false);
      return;
    }

    if (!isValidPassword(parentFormData.password)) {
      setConfirmation({ visible: true, variant: 'error', message: 'Password is too weak. Use at least 8 characters with uppercase, lowercase, numbers and special characters (e.g. @$!%*#?&).' });
      setLoading(false);
      return;
    }

    // Ensure at least one valid child exists. If none are saved yet, try to
    // auto-create one from the Child Profile card (showing inline validation
    // errors there if fields are incomplete/invalid).
    let effectiveChildren = childFormRef.current?.getChildren() ?? childrenData;

    if (!effectiveChildren || effectiveChildren.length === 0) {
      if (childFormRef.current) {
        const created = await childFormRef.current.addPendingChildIfValid();
        const afterChildren = childFormRef.current.getChildren();

        if (!created || !afterChildren || afterChildren.length === 0) {
          // Child Profile card will already show inline validation errors.
          setLoading(false);
          return;
        }

        effectiveChildren = afterChildren;
      } else {
        setConfirmation({ visible: true, variant: 'error', message: 'Please add at least 1 child.' });
        setLoading(false);
        return;
      }
    }

    const createUserResponse = await createUser(
      parentFormData.fname,
      parentFormData.lname,
      parentFormData.email,
      parentFormData.phone,
      parentFormData.password,
      effectiveChildren as any
    );

    if (createUserResponse.success && createUserResponse.jwt && createUserResponse.payer_id) {
      setConfirmation({ visible: true, variant: 'success', message: 'Account successfully created. Check your email for an activation link to activate your user account.' });
      setLoading(false);
    } else {
      setConfirmation({ visible: true, variant: 'error', message: createUserResponse.message });
    }
    setLoading(false);
  };

  const handleDismissConfirmation = () => {
    setConfirmation((c) => ({ ...c, visible: false }));
  };

  if (user && user.fname) {
    navigate('/profile');
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      <Box
        component="form"
        autoComplete="off"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <ParentForm
          title="Create Parent Profile"
          subtitle=""
          disableInputs={false}
          onParentDataChange={handleParentDataChange}
        />

        <ChildrenForm
          ref={childFormRef}
          onChildrenDataChange={(data: any[]) => setChildrenData(data as never[])}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button 
            component={Link} 
            to="/" 
            variant="outlined"
            size="large"
            sx={{ 
              px: 4,
              py: 1.5,
              minWidth: '120px',
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            size="large"
            disabled={loading} 
            onClick={handleSave}
            sx={{ 
              px: 4,
              py: 1.5,
              minWidth: '120px',
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        <ActionConfirmation
          id="signup-confirmation"
          visible={confirmation.visible}
          variant={confirmation.variant}
          message={confirmation.message}
          onDismiss={handleDismissConfirmation}
        />

        <GoogleReCaptcha
          onVerify={handleRecaptchaVerify}
          refreshReCaptcha={false}
        />
      </Box>
    </Container>
  );
};

export default SignUpPage;
